/**
 * Preview hook for the "By topic" revise flow. Given a set of curriculum
 * node IDs, fetches the per-type revisable step breakdown + total so the
 * CoachCard's Create-revision-area dialog can populate its QuestionTypeFilter
 * and max-questions slider with real counts before the learner starts.
 *
 * Hook is disabled when `nodeIds` is empty — the picker has nothing selected,
 * so there's nothing to preview yet.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import type { PreviewReviseScopeResponse } from '@formency/shared';

export const reviseScopePreviewKeys = {
  all: ['f', 'revise-scope-preview'] as const,
  forNodes: (nodeIds: string[]) => [...reviseScopePreviewKeys.all, [...nodeIds].sort()] as const,
};

/**
 * Fetch type + count breakdown for a set of curriculum nodes.
 *
 * The query key sorts `nodeIds` so {A, B} and {B, A} hit the same cache entry.
 * TanStack Query dedupes identical subsequent calls; 30s staleTime prevents
 * re-fetching when the learner rapidly toggles the same set.
 */
export function useReviseScopePreview(nodeIds: string[]) {
  return useQuery<PreviewReviseScopeResponse>({
    queryKey: reviseScopePreviewKeys.forNodes(nodeIds),
    queryFn: () =>
      api.post<PreviewReviseScopeResponse>('/me/study-sessions/revise/preview-scope', {
        nodeIds,
      }),
    enabled: nodeIds.length > 0,
    staleTime: 30_000,
  });
}
